import { useMemo } from 'react';

import { getAgentSummaryKey } from '@apis/rendall/queries/use-agent-summary';
import { getScannerSummaryKey } from '@apis/rendall/queries/use-scanner-summary';
import { useOrganizationContext } from '@packages/legacy/organization-context';

const baseKey = 'telemetry';

type ParamType = Record<string, unknown>;
type TelemetryBaseType = {
  all: ReadonlyArray<string>;
};

interface TelemetryType extends TelemetryBaseType {
  list: (param: ParamType) => ReadonlyArray<string | ParamType>;
  summary: () => ReadonlyArray<string>;
}

interface DetailTelemetryType extends TelemetryType {
  details: () => ReadonlyArray<string>;
  detail: (id: string) => ReadonlyArray<string>;
}

interface CloudSensorDetailType extends DetailTelemetryType {
  schema: () => ReadonlyArray<string>;
}

interface SitesDetailType {
  detail: (id: string) => ReadonlyArray<string>;
  list: () => ReadonlyArray<string>;
}

export type TelemetryQueryKeyProps = {
  telemetry: TelemetryBaseType;
  telemetryCloudSensors: CloudSensorDetailType;
  telemetryAgents: DetailTelemetryType;
  telemetryScanners: DetailTelemetryType;
  telemetryScanSchedules: DetailTelemetryType;
  telemetrySites: SitesDetailType;
};

export const useTelemetryQueryKey = (): TelemetryQueryKeyProps => {
  const { selectedOrganization } = useOrganizationContext();

  const telemetry = useMemo(
    () => ({
      all: [baseKey, selectedOrganization.id] as const,
    }),
    [selectedOrganization.id],
  );

  const telemetryAgents = useMemo(
    () => ({
      all: [...telemetry.all, 'agent'] as const,
      detail: (id: string) => [...telemetryAgents.details(), id] as const,
      details: () => [...telemetryAgents.all, 'detail'] as const,
      list: (param: ParamType) =>
        [...telemetryAgents.all, 'list', param] as const,
      summary: () => getAgentSummaryKey(selectedOrganization.id),
    }),
    [selectedOrganization.id, telemetry.all],
  );

  const telemetryCloudSensors = useMemo(
    () => ({
      all: [...telemetry.all, 'cloudSensor'] as const,
      detail: (id: string) => [...telemetryCloudSensors.details(), id] as const,
      details: () => [...telemetryCloudSensors.all, 'detail'] as const,
      list: (param: ParamType) =>
        [...telemetryCloudSensors.all, 'list', param] as const,
      schema: () => [...telemetryCloudSensors.details(), 'schema'] as const,
      summary: () => [...telemetryCloudSensors.all, 'summary'] as const,
    }),
    [telemetry.all],
  );

  const telemetryScanners = useMemo(
    () => ({
      all: [...telemetry.all, 'scanners'] as const,
      detail: (id: string) => [...telemetryScanners.details(), id] as const,
      details: () => [...telemetryScanners.all, 'detail'] as const,
      list: (param: ParamType) =>
        [...telemetryScanners.all, 'list', param] as const,
      summary: () => getScannerSummaryKey(selectedOrganization.id),
    }),
    [selectedOrganization.id, telemetry.all],
  );

  const telemetryScanSchedules = useMemo(
    () => ({
      all: [...telemetry.all, 'scanSchedules'] as const,
      detail: (id: string) =>
        [...telemetryScanSchedules.details(), id] as const,
      details: () => [...telemetryScanSchedules.all, 'detail'] as const,
      list: (param: ParamType) =>
        [...telemetryScanSchedules.all, 'list', param] as const,
      summary: () => [...telemetryScanSchedules.all, 'summary'] as const,
    }),
    [telemetry.all],
  );

  const telemetrySites = useMemo(
    () => ({
      all: [...telemetry.all, 'sites'] as const,
      detail: (id: string) => [...telemetrySites.all, id, 'detail'] as const,
      list: () => [...telemetrySites.all, 'list'] as const,
    }),
    [telemetry.all],
  );

  return {
    telemetry,
    telemetryAgents,
    telemetryCloudSensors,
    telemetryScanSchedules,
    telemetryScanners,
    telemetrySites,
  };
};
